import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "quantityGroup", "amountGroup",
    "quantityField", "amountField",
    "calculatedAmountGroup", "calculatedQuantityGroup",
    "calculatedAmountField", "calculatedQuantityField",
    "calculationInfo", "calculationText",
    "submitButton"
  ]
  
  static values = {
    subscriptionId: Number,
    calculatedAmountText: String,
    calculatedAmountSuffix: String,
    calculatedQuantityText: String,
    calculatedQuantitySuffix: String,
    authorizedOrderType: String
  }

  connect() {
    console.log("Subscriptionevergreen controller connected")

    this.selectedNominalValue = null
    
    // Récupérer la valeur nominale si une part est déjà sélectionnée
    const selectedShare = this.element.querySelector('input[name="subscription[share_id]"]:checked')
    if (selectedShare) {
      this.selectedNominalValue = parseFloat(selectedShare.dataset.nominalValue)
    }
    
    // Gérer l'order_type en fonction de authorized_order_type
    if (this.authorizedOrderTypeValue === 'both') {
      const orderTypeSelected = this.element.querySelector('input[name="subscription[order_type]"]:checked')
      if (!orderTypeSelected) {
        const amountRadio = this.element.querySelector('input[name="subscription[order_type]"][value="amount"]')
        if (amountRadio) {
          amountRadio.checked = true
        }
      }
    }

    // Initialiser la visibilité des champs
    this.updateFieldsVisibility()

    // Vérifier l'état initial du formulaire
    this.validateForm()

    // Ajouter un listener sur la soumission du formulaire
    this.element.addEventListener('submit', this.handleSubmit.bind(this))
  }

  orderTypeChanged(event) {
    console.log("Order type changed to:", event.target.value)
    this.updateFieldsVisibility()
    this.calculate()
    this.validateForm()
  }

  getOrderType() {
    if (this.authorizedOrderTypeValue && this.authorizedOrderTypeValue !== 'both') {
      return this.authorizedOrderTypeValue
    }

    const orderTypeField = this.element.querySelector('input[name="subscription[order_type]"]:checked') ||
                          this.element.querySelector('input[name="subscription[order_type]"]')
    return orderTypeField?.value || 'amount'
  }

  updateFieldsVisibility() {
    const orderType = this.getOrderType()
    console.log("Updating visibility for order type:", orderType)
    
    if (orderType === 'quantity') {
      if (this.selectedNominalValue && this.amountFieldTarget.value) {
        const amount = parseFloat(this.amountFieldTarget.value)
        if (amount > 0) {
          const calculatedQuantity = (amount / this.selectedNominalValue).toFixed(4)
          this.quantityFieldTarget.value = calculatedQuantity
        }
      }
      
      this.quantityGroupTarget.style.display = 'block'
      this.amountGroupTarget.style.display = 'none'
      this.calculatedAmountGroupTarget.style.display = 'none'
      this.calculatedQuantityGroupTarget.style.display = 'none'
      
      this.quantityFieldTarget.required = true
      this.amountFieldTarget.required = false
      
      this.calculate()
      
    } else {
      if (this.selectedNominalValue && this.quantityFieldTarget.value) {
        const quantity = parseFloat(this.quantityFieldTarget.value)
        if (quantity > 0) {
          const calculatedAmount = (quantity * this.selectedNominalValue).toFixed(2)
          this.amountFieldTarget.value = calculatedAmount
          this.triggerReflex()
        }
      }
      
      this.quantityGroupTarget.style.display = 'none'
      this.amountGroupTarget.style.display = 'block'
      this.calculatedAmountGroupTarget.style.display = 'none'
      this.calculatedQuantityGroupTarget.style.display = 'none'
      
      this.quantityFieldTarget.required = false
      this.amountFieldTarget.required = true
      
      this.calculate()
    }
  }

  shareChanged(event) {
    console.log("Share changed, nominal value:", event.target.dataset.nominalValue)
    this.selectedNominalValue = parseFloat(event.target.dataset.nominalValue)
    this.calculate()
    this.validateForm()
  }

  quantityChanged(event) {
    console.log("Quantity changed to:", event.target.value)
    this.calculate()
    this.validateForm()
  }

  amountChanged(event) {
    console.log("Amount changed to:", event.target.value)
    this.calculate()
    this.validateForm()
  }

  calculate() {
    const orderType = this.getOrderType()
    
    if (!this.selectedNominalValue) {
      this.hideCalculations()
      return
    }

    if (orderType === 'quantity') {
      const quantity = parseFloat(this.quantityFieldTarget.value)
      
      if (quantity && quantity > 0) {
        const calculatedAmount = quantity * this.selectedNominalValue
        
        // Mettre à jour le champ amount caché pour déclencher le Reflex
        this.amountFieldTarget.value = calculatedAmount.toFixed(2)

        // Déclencher manuellement le Reflex pour vérifier les critères (< 100 000)
        this.triggerReflex()
        
        // Afficher le montant calculé
        this.calculatedAmountFieldTarget.value = calculatedAmount.toFixed(2)
        this.calculatedAmountGroupTarget.style.display = 'block'
        
        // Utiliser l'interpolation JavaScript pour construire le message complet
        const message = `${this.calculatedAmountTextValue} ${calculatedAmount.toFixed(2)} ${this.calculatedAmountSuffixValue}`
        this.showCalculationInfo(message)
      } else {
        this.hideCalculations()
        this.amountFieldTarget.value = ''
      }
      
    } else if (orderType === 'amount') {
      const amount = parseFloat(this.amountFieldTarget.value)
      
      if (amount && amount > 0) {
        const calculatedQuantity = (amount / this.selectedNominalValue).toFixed(4)
        
        // Afficher la quantité calculée
        this.calculatedQuantityFieldTarget.value = calculatedQuantity
        this.calculatedQuantityGroupTarget.style.display = 'block'
        
        // Utiliser l'interpolation JavaScript pour construire le message complet
        const message = `${this.calculatedQuantityTextValue} ${calculatedQuantity} ${this.calculatedQuantitySuffixValue}`
        this.showCalculationInfo(message)
      } else {
        this.hideCalculations()
      }
    }
  }

  triggerReflex() {
    // Déclencher manuellement l'événement change sur le champ amount, pour activer le Reflex SubscriptionamountReflex#validate
    const changeEvent = new Event('change', { bubbles: true })
    this.amountFieldTarget.dispatchEvent(changeEvent)
  }

  validateForm() {
    const orderType = this.getOrderType()
    const shareSelected = this.element.querySelector('input[name="subscription[share_id]"]:checked')
    
    let isValid = false
    
    if (shareSelected) {
      if (orderType === 'quantity') {
        const quantity = parseFloat(this.quantityFieldTarget.value)
        isValid = quantity && quantity > 0
      } else {
        const amount = parseFloat(this.amountFieldTarget.value)
        isValid = amount && amount > 0
      }
    }
    
    // Activer/désactiver le bouton submit
    this.submitButtonTarget.disabled = !isValid
    console.log("Form validation:", isValid ? "valid" : "invalid")
  }

  showCalculationInfo(message) {
    this.calculationTextTarget.textContent = message
    this.calculationInfoTarget.style.display = 'block'
  }

  hideCalculations() {
    this.calculationInfoTarget.style.display = 'none'
    this.calculatedAmountGroupTarget.style.display = 'none'
    this.calculatedQuantityGroupTarget.style.display = 'none'
  }

  handleSubmit(event) {
    const orderType = this.getOrderType()

    console.log("Form submit - Order type:", orderType)

    if (orderType === 'quantity') {
      console.log("Submitting with quantity, calculated amount:", this.amountFieldTarget.value)
    } else {
      this.quantityFieldTarget.value = ''
      console.log("Submitting with amount, quantity cleared")
    }
  }
}